import { useState, useCallback } from "react";
import axios from "axios";
import { z } from "zod";
import api from "@/store/api";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { ProfileSchema } from "@/schema";

const useProfile = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaveLoading, setisSaveLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const registerUser = useCallback(
    async (values: z.infer<typeof ProfileSchema>) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await api.post("/api/profile", values);
        setIsLoading(false);
        return response.data;
      } catch (err) {
        setIsLoading(false);
        if (axios.isAxiosError(err)) {
          setError(
            err.response?.data?.message || t("alerts.error.registeringUser")
          );
        } else {
          setError(t("alerts.error.unexpectedError"));
        }
        throw err;
      }
    },
    [t]
  );

  const getSellerProfile = useCallback(async () => {
    try {
      const response = await api.get("/web/users/getAllSellers");
      return response.data;
    } catch (err) {
      console.error(err);
    }
  }, []);

  const updateUser = useCallback(
    async (values: z.infer<typeof ProfileSchema>) => {
      setisSaveLoading(true);
      setError(null);
      try {
        // Token is automatically added by the global interceptor
        const response = await api.post(`/web/auth/upsert`, values);
        setisSaveLoading(false);
        return response.data;
      } catch (err) {
        setisSaveLoading(false);
        if (axios.isAxiosError(err)) {
          setError(
            err.response?.data?.message || t("alerts.error.updatingProfile")
          );
        } else {
          setError(t("alerts.error.unexpectedError"));
        }
        throw err;
      }
    },
    [t]
  );

  const getUserProfile = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Token is automatically added by the global interceptor
      const response = await api.get(`/web/authInfo/profile/`);
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          return null;
        }
        setError(
          err.response?.data?.message || t("alerts.error.fetchingProfile")
        );
      } else {
        setError(t("alerts.error.unexpectedError"));
      }
      throw err;
    }
  }, [t]);

  return {
    registerUser,
    updateUser,
    getUserProfile,
    isLoading,
    getSellerProfile,
    isSaveLoading,
    error,
  };
};

export default useProfile;
